import axios from "axios";
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  List,
  TextField,
} from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import localize from "../../../common/localize";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  getInputLabelProps,
  getInputProps,
  PopperFullWidth,
} from "../../../common/appStyles";
import { GetDateInYYYYMMDDWithoutTimeFormat } from "../../../common/utils";
import Combobox from "../../../components/common/Combobox";
import useGlobalContext from "../../../hooks/useGlobalContext";
import {
  getMovementTypeColor,
  getMovementTypeName,
  MovementTypeEnum,
} from "../../../common/EntitiesManagement";
import { Add } from "@mui/icons-material";
import SupplySearch from "../../../components/common/SupplySearch";
import ModalBackground from "../../../components/common/ModalBackground";
import SupplyQuantity from "../../../components/common/SupplyQuantity";
import NotificationItem from "../../../components/common/NotificationItem";
import SupplyItem from "../../supplies/components/SupplyItem";
import SupplyFilter from "../../supplies/components/SupplyFilter";
import ExpandableView from "../../../components/common/ExpandableView";
import CreatedByText from "../../../components/common/CreatedByText";

export default function AddInventoryMovement({ model, setModel }) {
  const {
    config,
    // WAREHOUSE_ENDPOINT,
    INVENTORY_MOVEMENT_TYPE_ENDPOINT,
    INVENTORY_MOVEMENT_HEADER_ENDPOINT,
  } = useGlobalContext();
  const [movementTypes, setMovementTypes] = useState();
  const [descriptionCount, setDescriptionCount] = useState(0);
  const [showSearch, setShowSearch] = useState(false);
  const [supplies, setSupplies] = useState([]);
  // const [warehouses, setWarehouses] = useState([]);
  const [searchList, setSearchList] = useState([]);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [detailObservation, setDetailObservation] = useState("");
  const [suppliesFiltered, setSuppliesFiltered] = useState([]);
  const maximumCharactersForDescription = 255;

  /* const getWarehouses = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${WAREHOUSE_ENDPOINT}`, requestConfig);

    setWarehouses(data?.data);
  }; */

  /* const handleWarehouseChange = (e) => {
    setModel({
      ...model,
      Warehouse: e ?? null,
      WarehouseId: e.Id ?? 0,
    });
  }; */

  useEffect(() => {
    setModel({
      ...model,
      MovementDetail:
        !!supplies && supplies.length > 0
          ? supplies.map((supply) => ({
              SupplyId: supply.Id,
              Quantity: supply.Quantity,
              Observation: supply.Observation,
            }))
          : [],
    });
  }, [supplies]);

  useEffect(() => {
    if (searchList && searchList.length > 0) {
      setDetailObservation("");
      setQuantity(1);
      setShowQuantityModal(true);
    }
  }, [searchList]);

  const getInventoryMovementDetail = async (headerId) => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(
      `${INVENTORY_MOVEMENT_HEADER_ENDPOINT}/${headerId}/detail`,
      requestConfig
    );
    setSupplies(data?.data?.Data);
    setSuppliesFiltered(data?.data?.Data);
  };

  const getInventoryMovementTypes = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(
      `${INVENTORY_MOVEMENT_TYPE_ENDPOINT}/`,
      requestConfig
    );

    setMovementTypes(data?.data);
  };

  useEffect(() => {
    getInventoryMovementTypes();
    if (model.Id > 0) {
      getInventoryMovementDetail(model.Id);
    }
    // getWarehouses();
  }, []);

  useEffect(() => {
    if (model.Observation) {
      setDescriptionCount(model.Observation.length);
    }
  }, [model?.Observation]);

  const handleChange = (e) => {
    setModel({
      ...model,
      [e.target.name]: e.target.value,
    });
  };

  const handleMovementDateChange = (dateValue) => {
    try {
      if (!dateValue) {
        setModel({
          ...model,
          MovementDate: GetDateInYYYYMMDDWithoutTimeFormat(new Date()),
        });
        return;
      }

      setModel({
        ...model,
        MovementDate: GetDateInYYYYMMDDWithoutTimeFormat(dateValue),
      });
    } catch {
      console.error("Invalid date");
    }
  };

  const handleMovementTypeChange = (value) => {
    if (!value) {
      setModel({
        ...model,
        MovementType: null,
        MovementTypeId: 0,
      });
      return;
    }

    setModel({
      ...model,
      MovementType: value,
      MovementTypeId: value.Id,
    });
  };

  const handleAddButton = () => {
    setShowSearch(true);
  };

  const removeEventFromList = (supplyId) => {
    const index = supplies.findIndex((x) => x.Id === supplyId);
    if (index >= 0) {
      supplies.splice(index, 1);
      setSupplies([...supplies]);
    }
  };

  const movementTypeHasNotBeenSelected = () =>
    !model?.MovementTypeId || model?.MovementTypeId === 0;

  useEffect(() => {
    setSuppliesFiltered(supplies);
  }, [supplies]);

  return (
    <Box sx={{ width: "100%" }}>
      {showSearch && (
        <SupplySearch
          setResultList={setSearchList}
          showSearch={showSearch}
          setShowSearch={setShowSearch}
          singleSearch
          warehouse={model?.Warehouse}
          blockWarehouse={!!model?.Warehouse}
          is_in_movement={
            model?.MovementType?.MovementType === MovementTypeEnum.In
          }
        />
      )}

      {showQuantityModal && (
        <ModalBackground
          title={localize.assign_quantity}
          validators={
            !quantity ||
            quantity === 0 ||
            (searchList[0].Quantity === 0 &&
              model?.MovementType?.MovementType === MovementTypeEnum.Out)
          }
          showModal={showQuantityModal}
          setShowModal={setShowQuantityModal}
          okButtonText={localize.accept}
          cancelButtonText={localize.cancel}
          onClickToSubmit={() => {
            const index = supplies.findIndex((x) => x.Id === searchList[0].Id);
            if (index >= 0) {
              supplies[index] = {
                ...searchList[0],
                Quantity: quantity,
                Observation: detailObservation,
              };

              setSupplies([...supplies]);
              setShowQuantityModal(false);
              return;
            }

            setSupplies([
              ...supplies,
              {
                ...searchList[0],
                Quantity: quantity,
                Observation: detailObservation,
              },
            ]);
            setShowQuantityModal(false);
          }}
          onClickToCancel={() => {
            setSearchList([]);
            setShowQuantityModal(false);
            setDetailObservation("");
          }}
          Component={
            <SupplyQuantity
              supply={searchList[0]}
              quantity={quantity}
              setQuantity={setQuantity}
              movementType={model?.MovementType?.MovementType}
              detailObservation={detailObservation}
              setDetailObservation={setDetailObservation}
            />
          }
        />
      )}

      <ExpandableView
        components={[
          {
            Name: localize.general_information,
            Component: (
              <Box>
                <div className="w-full flex gap-2 my-4">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      disabled={model.Id > 0}
                      label={`${localize.movement_date}`}
                      className="w-full"
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      PopperComponent={PopperFullWidth}
                      value={model?.MovementDate}
                      onChange={handleMovementDateChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: "",
                          }}
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            style: { fontSize: "1em", paddingBottom: "10px" },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>

                  <TextField
                    disabled={model.Id > 0}
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    value={model.Code}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    id="Code"
                    label={localize.code}
                    name="Code"
                    variant="outlined"
                  />
                </div>

                <div className="w-full flex gap-2 mb-4">
                  <Combobox
                    disabled={model.Id > 0}
                    fullWidth
                    id="MovementType"
                    fieldLabel={localize.movement_type}
                    fieldOptions={movementTypes?.Data ?? []}
                    fieldValue={model?.MovementType}
                    handleValueChange={handleMovementTypeChange}
                    popperFullWidth={PopperFullWidth}
                  />

                  {model?.MovementType &&
                    model?.MovementType?.MovementType > 0 && (
                      <Chip
                        className="chipContainer"
                        style={{
                          backgroundColor: getMovementTypeColor(
                            model?.MovementType?.MovementType
                          ),
                          color: "white",
                          borderColor: getMovementTypeColor(
                            model?.MovementType?.MovementType
                          ),
                        }}
                        label={getMovementTypeName(
                          model?.MovementType?.MovementType
                        )}
                      />
                    )}
                </div>

                {/*<div className="w-full flex gap-2 mb-4">
                  <Combobox
                    disabled={
                      model.Id > 0 || (model?.warehouse && supplies.length > 0)
                    }
                    fullWidth
                    id="Warehouse"
                    fieldLabel={localize.warehouse}
                    fieldOptions={warehouses?.Data ?? []}
                    fieldValue={model?.Warehouse}
                    handleValueChange={handleWarehouseChange}
                    popperFullWidth={PopperFullWidth}
                  />
                </div>*/}

                {model &&
                  model?.WarehouseId > 0 &&
                  model?.MovementType?.MovementType === 2 && (
                    <div className="w-full flex gap-2 mb-4">
                      <Alert severity="warning">
                        <label className="text-xs">
                          {
                            localize.warning_output_inventory_movement_for_warehouse
                          }
                        </label>
                      </Alert>
                    </div>
                  )}

                <div className="w-full flex flex-wrap mb-4">
                  <FormControl fullWidth>
                    <TextField
                      disabled={model.Id > 0}
                      InputLabelProps={getInputLabelProps()}
                      inputProps={{
                        style: {
                          fontSize: "0.8em",
                        },
                        maxLength: maximumCharactersForDescription,
                      }}
                      size="small"
                      fullWidth
                      multiline
                      rows={3}
                      id="outlined-basic"
                      name="Observation"
                      label={localize.observations}
                      variant="outlined"
                      onChange={handleChange}
                      value={model.Observation}
                      helperText={`${descriptionCount}/${maximumCharactersForDescription}`}
                    />
                  </FormControl>
                </div>
              </Box>
            ),
          },
          {
            Name: localize.supplies,
            Component: (
              <Box
                className="flex flex-col my-4 gap-2"
                sx={{ height: "280px" }}
              >
                <Box className="flex gap-2 justify-between items-center">
                  <Alert
                    severity={
                      movementTypeHasNotBeenSelected() ? "warning" : "info"
                    }
                    className={model.Id > 0 ? "w-full" : ""}
                  >
                    <label className="text-xs">
                      {movementTypeHasNotBeenSelected()
                        ? localize.movement_type_not_selected
                        : localize.inventory_movement_supplies_message}
                    </label>
                  </Alert>
                  {(!model.Id || model.Id === 0) && (
                    <Button
                      disabled={movementTypeHasNotBeenSelected()}
                      style={{ width: "120px" }}
                      size="small"
                      variant="contained"
                      color="success"
                      className="app_general_button_data app_success_button btn-sm p-2"
                      onClick={() => {
                        setSearchList([]);
                        setQuantity(1);
                        handleAddButton();
                      }}
                    >
                      <span className="mr-2">{localize.add}</span>
                      <Add />
                    </Button>
                  )}
                </Box>
                <Divider />
                <Box className="flex flex-col gap-2">
                  {supplies && supplies.length > 0 && (
                    <SupplyFilter
                      fullData={supplies}
                      setDataFiltered={setSuppliesFiltered}
                    />
                  )}
                  <List className="flex flex-col gap-2">
                    {suppliesFiltered && suppliesFiltered.length > 0 ? (
                      suppliesFiltered.map((item) => (
                        <SupplyItem
                          item={item}
                          onDelete={
                            !model.Id || model.Id === 0
                              ? () => {
                                  removeEventFromList(item.Id);
                                }
                              : undefined
                          }
                        />
                      ))
                    ) : (
                      <NotificationItem
                        notification={{
                          message: localize.no_data,
                        }}
                      />
                    )}
                  </List>
                </Box>
              </Box>
            ),
          },
        ]}
      />

      <CreatedByText entity={model} />
    </Box>
  );
}
